import { createTheme } from '@mui/material/styles';
import { amber, blue, emerald, green, red, slate } from '@/lib/colours';

export const theme = createTheme({
  palette: {
    primary: {
      main: blue['600'],
      light: blue['200'],
      dark: blue['900'],
    },
    secondary: {
      main: emerald['600'],
      light: emerald['200'],
      dark: emerald['800'],
    },
    error: {
      main: red['600'],
      light: red['200'],
      dark: red['800'],
    },
    warning: {
      main: amber['600'],
      light: amber['200'],
      dark: amber['800'],
    },
    info: {
      main: blue['600'],
      light: blue['200'],
      dark: blue['800'],
    },
    success: {
      main: green['600'],
      light: green['200'],
      dark: green['800'],
    },
  },
  typography: {
    fontFamily: 'inherit',
    h1: {},
    h2: {},
    h3: {},
    h4: {},
    h5: {},
    h6: {},
    subtitle1: undefined,
    subtitle2: undefined,
    body1: {},
    body2: undefined,
    caption: undefined,
    displaylarge: {
      fontSize: '57px',
      fontWeight: 400,
      lineHeight: '64px',
      letterSpacing: '-0.25px',
    },
    displaymedium: {
      fontSize: '45px',
      fontWeight: 400,
      lineHeight: '52px',
    },
    displaysmall: {
      fontSize: '36px',
      fontWeight: 400,
      lineHeight: '44px',
    },
    headlinelarge: {
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '40px',
    },
    headlinemedium: {
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
    },
    headlinesmall: {
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '32px',
    },
    titlelarge: {
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: '28px',
    },
    titlemedium: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.1px',
    },
    titlesmall: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    labellarge: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    labelmedium: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      letterSpacing: '0.5px',
    },
    labelsmall: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: '16px',
      letterSpacing: '0.5px',
    },
    bodylarge: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.5px',
    },
    bodymedium: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.25px',
    },
    bodysmall: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0.4px',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displaylarge: 'h1',
          displaymedium: 'h1',
          displaysmall: 'h1',
          headlinelarge: 'h2',
          headlinemedium: 'h2',
          headlinesmall: 'h2',
          titlelarge: 'h3',
          titlemedium: 'h3',
          titlesmall: 'h3',
          labellarge: 'p',
          labelmedium: 'p',
          labelsmall: 'p',
          bodylarge: 'p',
          bodymedium: 'p',
          bodysmall: 'p',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${slate[200]}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'none',
          letterSpacing: '0.1px',
          lineHeight: '20px',
          boxShadow: 'none',
        },
        text: {
          padding: '6px 16px',
          '&:hover': {
            backgroundColor: blue[100],
          },
        },
        sizeMedium: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
      variants: [
        {
          props: { variant: 'active' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.dark,
          }),
        },
      ],
    },
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          marginTop: '12px',
          marginBottom: '12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 40px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${slate[200]}`,
          borderBottom: `1px solid ${slate[200]}`,
          padding: '24px 40px',
          paddingTop: '24px !important',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: '12px',
          },
        },
      },
    },
  },
});
